import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import * as yup from "yup";

import MyButton from "../../components/MyButton";
import MyTextField from "../../components/inputs/MyTextField";
import { useBoardItem } from "../../hooks/useBoardItem";
import { SiteContext } from "../../context/SiteProvider";
import MyTitle from "../../components/MyTitle";
import { AuthContext } from "../../context/AuthProvider";
import FileItem from "../../components/inputs/MyInputFile/FileItem";
import { useBoardItemComment } from "../../hooks/useBoardItemComment";
import MyLoading from "../../components/MyLoading";
import DeleteBoardItemCommentDialog from "../Common/DeleteBoardItemCommentDialog";
import { PopupContext } from "../../context/PopupProvider";

const AnswerList = ({
  board,
  boardItem,
  selectComment,
  setSelectComment,
  setReplyMode,
}) => {
  const { verifyPasswordViewBoardMutation } = useBoardItem();
  const { getPrivateCommentsMutation, useGetBoardItemComments } =
    useBoardItemComment();
  const { setLoadingScreen } = useContext(SiteContext);
  const { isAuthenticated, isAdmin, profile } = useContext(AuthContext);
  const { openPopup } = useContext(PopupContext);
  const queryClient = useQueryClient();

  const privateViewToken = new URLSearchParams(window.location.search).get(
    "privateViewToken"
  );

  const { data, isLoading } = useGetBoardItemComments(
    {
      size: 10000,
      page: 1,
      sort: "createdDate,Asc",
      boardCode: board?.boardCode,
      itemNo: boardItem?.itemNo,
      rawPrivateViewToken: privateViewToken,
      useComment: board?.options?.useComment,
    },
    (data) => data.data
  );

  const [item, setItem] = useState(null);
  const [hiddenPasswordForm, setHiddenPasswordForm] = useState(true);

  const initValues = {
    writerPassword: "",
  };

  const validationSchema = yup.object({
    writerPassword: yup.string().required("필수 입력 항목입니다."),
  });

  const methods = useForm({
    defaultValues: initValues,
    resolver: yupResolver(validationSchema),
  });

  const openPopupDelete = (commentNo) =>
    openPopup(
      <DeleteBoardItemCommentDialog
        mode="reply"
        boardCode={board?.boardCode}
        itemNo={boardItem?.itemNo}
        commentNo={commentNo}
        deleteMessage="답변이 삭제되었습니다."
        onRedirect={() => {
          setSelectComment(null);
          setItem(null);
          setReplyMode(false);
          queryClient.invalidateQueries({
            queryKey: ["/board-item/{boardCode}/{itemNo}"],
          });
        }}
      />
    );

  const onSubmit = async (data) => {
    if (board?.boardCode) {
      setLoadingScreen(true);

      const payload = {
        writerPassword: data.writerPassword,
      };

      verifyPasswordViewBoardMutation.mutate(
        {
          boardCode: board?.boardCode,
          itemNo: boardItem?.itemNo,
          commentNo: item.commentNo,
          payload,
        },
        {
          onSuccess: (res) => {
            if (res?.privateViewToken) {
              methods.clearErrors("writerPassword");
              getPrivateCommentsMutation.mutate(
                {
                  boardCode: board?.boardCode,
                  itemNo: boardItem?.itemNo,
                  commentNo: item.commentNo,
                  rawPrivateViewToken: res.privateViewToken,
                  page: 1,
                  size: 10000,
                  sort: "createdDate,Desc",
                },
                {
                  onSuccess: (res2) => {
                    if (res2?.content?.length) {
                      setItem({ ...res2.content[0], isPrivate: false });
                      setHiddenPasswordForm(true);
                    }
                  },
                }
              );
            } else {
              methods.setError("writerPassword", {
                message: "댓글을 인증하는 동안 오류가 발생했습니다.",
              });
              return;
            }
          },
          onError: () => {
            methods.setError("writerPassword", {
              message: "댓글을 인증하는 동안 오류가 발생했습니다.",
            });
            return;
          },
          onSettled: () => {
            setLoadingScreen(false);
          },
        }
      );
    } else {
      alert("게시글 카테고리가 존재하지 않습니다.");
    }
  };

  const onSelectComment = () => {
    const result = { ...item };
    result.hidden = true;

    setItem(null);
    setSelectComment(result);
  };

  useEffect(() => {
    if (data?.content?.length && !isLoading && !selectComment) {
      setItem({ ...data.content[0], hidden: false });
    }
  }, [isLoading, data, selectComment]);

  useEffect(() => {
    if (!item?.isPrivate || isAuthenticated) {
      setHiddenPasswordForm(true);
    } else {
      setHiddenPasswordForm(false);
    }
  }, [item, isAuthenticated]);

  return isLoading ? (
    <MyLoading className="h-[150px]" />
  ) : !!item ? (
    <div className="overflow-x-auto max-w-full">
      <MyTitle title="답변 내용" className="mb-4" />
      <table className="w-full border-collapse mb-5 border-t-2 border-primary">
        <tbody>
          <tr>
            <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
              답변내용
            </th>
            <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
              {!hiddenPasswordForm ? (
                <FormProvider {...methods}>
                  <form onSubmit={methods.handleSubmit(onSubmit)}>
                    <p className="text-[#888]">
                      비밀글 기능으로 보호된 답변글입니다.
                    </p>
                    <p className="text-[#888] mb-2">
                      작성자와 관리자만 열람하실 수 있습니다.
                    </p>
                    <div className="flex flex-wrap items-stretch gap-2 gap-y-1 mt-2">
                      <MyTextField
                        id="writerPassword"
                        name="writerPassword"
                        type="password"
                        placeholder="비밀번호를 입력하세요."
                        className="xs:max-w-[250px] w-full"
                      />
                      <MyButton
                        color="primary"
                        size="small"
                        className="h-[56px]"
                        type="submit"
                      >
                        확인
                      </MyButton>
                    </div>
                  </form>
                </FormProvider>
              ) : isAuthenticated &&
                profile?.userId !== boardItem?.writer?.userId &&
                !isAdmin &&
                item.isPrivate ? (
                <>
                  <p className="text-[#888]">
                    비밀글 기능으로 보호된 답변글입니다.
                  </p>
                  <p className="text-[#888] mb-2">
                    작성자와 관리자만 열람하실 수 있습니다.
                  </p>
                </>
              ) : (
                item?.comment || "-"
              )}
            </td>
          </tr>
          <tr>
            <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
              첨부파일
            </th>
            <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
              {item?.attachedFiles?.length > 0 ? (
                <div>
                  {item.attachedFiles.map((c) => (
                    <FileItem key={c.fileId} file={c} />
                  ))}
                </div>
              ) : (
                "-"
              )}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="flex items-center justify-end sm:gap-4 gap-2">
        <MyButton
          variant="outlined"
          className="sm:min-w-[120px]"
          onClick={() => openPopupDelete(item.commentNo)}
        >
          <span className="font-semibold">삭제</span>
        </MyButton>
        <MyButton
          variant="contained"
          color="primary"
          className="sm:min-w-[120px]"
          onClick={onSelectComment}
        >
          답변 수정하기
        </MyButton>
      </div>
    </div>
  ) : null;
};

export default AnswerList;
